import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons, useImageViewer } from '@ohif/ui-next';
import { authService } from '../../../../../platform/app/src/custom/auth/authService';
import { getUserRoleFromAuthUser } from '../../../../../platform/app/src/custom/utils/roleUtils';
import { isPatientMode } from '../../../../../platform/app/src/custom/utils/patientMode';
import { currentUserService } from '../../../../../platform/app/src/custom/services/currentUserService';
import type { CurrentUserResponse } from '../../../../../platform/app/src/custom/services/currentUserService';
import { fetchAuthenticatedUser } from '../../../../../platform/app/src/custom/utils/apiClient';
import { useReportPanelStudyStore } from '../../stores/useReportPanelStudyStore';
import {
  fetchStudyReportInfo,
  isViewableReportStatus,
} from '../../utils/studyReportStatus';

function HeaderReportInfo({
  servicesManager,
  appConfig,
  extensionManager,
}: withAppTypes & { extensionManager?: { getActiveDataSource: () => unknown[] | unknown } }) {
  const { t } = useTranslation('PatientView');
  const { panelService } = servicesManager.services;
  const { StudyInstanceUIDs } = useImageViewer();
  const openedStudyInstanceUID = StudyInstanceUIDs?.[0] || null;
  const [hasReport, setHasReport] = useState<boolean | null>(null);
  const [reportStatus, setReportStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  /** For referring physicians: true = may view reports, false = denied, null = resolving from /me */
  const [referringPhysicianReportAccess, setReferringPhysicianReportAccess] = useState<
    boolean | null
  >(() => {
    if (getUserRoleFromAuthUser(authService.getUser()) !== 'ReferringPhysician') {
      return null;
    }
    const db = currentUserService.getUserFromDatabase();
    const ra = db?.report_access;
    if (db && (ra === true || ra === 'true' || ra === 1)) {
      return true;
    }
    if (db && (ra === false || ra === 'false' || ra === 0)) {
      return false;
    }
    return null;
  });

  // Check role-based report CTA visibility
  const user = authService.getUser();
  const userRole = getUserRoleFromAuthUser(user);
  const isRadiologist = userRole === 'Radiologist';
  const isReferringPhysician = userRole === 'ReferringPhysician';
  const isTechnician = userRole === 'Technician';
  const isAdminOrSuperAdmin = userRole === 'Admin' || userRole === 'SuperAdmin';
  const patientMode = isPatientMode();
  // For Referring Physician, show when report is approved or pending (with report_access)
  // For Radiologist, show always (can generate/view)
  const shouldShowReportButton = isRadiologist || isReferringPhysician;

  // Resolve report_access for referring physicians (from /me — may load after viewer opens)
  useEffect(() => {
    if (!isReferringPhysician || patientMode) {
      return;
    }
    const db = currentUserService.getUserFromDatabase();
    const rawAccess = db?.report_access;
    if (db && (rawAccess === true || rawAccess === 'true' || rawAccess === 1)) {
      setReferringPhysicianReportAccess(true);
      return;
    }
    if (db && (rawAccess === false || rawAccess === 'false' || rawAccess === 0)) {
      setReferringPhysicianReportAccess(false);
      return;
    }
    let cancelled = false;
    fetchAuthenticatedUser()
      .then(res => {
        const body = res.data as CurrentUserResponse;
        if (body?.success && body.data && body.fromDatabase) {
          currentUserService.setCurrentUser(body);
        }
        if (!cancelled) {
          const acc = currentUserService.getUserFromDatabase()?.report_access;
          setReferringPhysicianReportAccess(
            acc === true || acc === 'true' || acc === 1
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReferringPhysicianReportAccess(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isReferringPhysician, patientMode]);

  // Report status for the study opened from the worklist (URL StudyInstanceUIDs[0]).
  useEffect(() => {
    const checkReportExists = async () => {
      if (!openedStudyInstanceUID) {
        setHasReport(null);
        setReportStatus(null);
        return;
      }

      setIsLoading(true);

      try {
        const info = await fetchStudyReportInfo(openedStudyInstanceUID, extensionManager, {
          treatQidoApprovedAsReport:
            isReferringPhysician || isTechnician || patientMode || isAdminOrSuperAdmin,
        });
        setHasReport(info.hasReport);
        setReportStatus(info.status);
      } catch (error) {
        console.warn('Error checking for report:', error);
        setHasReport(false);
        setReportStatus(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkReportExists();
  }, [
    openedStudyInstanceUID,
    extensionManager,
    isReferringPhysician,
    isTechnician,
    patientMode,
    isAdminOrSuperAdmin,
  ]);

  const handleClick = () => {
    if (openedStudyInstanceUID) {
      useReportPanelStudyStore.getState().setTargetStudyInstanceUID(openedStudyInstanceUID);
    }
    panelService.activatePanel('@ohif/extension-radiology-report.panelModule.reportPanel', true);
  };

  // Peer Review → Review opens viewer with peerReviewId; auto-open Report panel.
  useEffect(() => {
    const peerReviewId = new URLSearchParams(window.location.search).get('peerReviewId');
    if (!peerReviewId || !openedStudyInstanceUID || !isRadiologist) {
      return;
    }
    useReportPanelStudyStore.getState().setTargetStudyInstanceUID(openedStudyInstanceUID);
    panelService.activatePanel('@ohif/extension-radiology-report.panelModule.reportPanel', true);
  }, [openedStudyInstanceUID, isRadiologist, panelService]);

  // For Referring Physician: show "View" only if /me has report_access, report exists, and status is approved or pending
  // For Admin/SuperAdmin / Technician: show "View" when report exists and status is approved or pending
  // For Radiologist: show "View" or "Generate Report" based on hasReport
  const isReportViewable = isViewableReportStatus(reportStatus);
  const referringPhysicianCanViewReport =
    referringPhysicianReportAccess === true;
  const shouldShowForReferringPhysician =
    isReferringPhysician &&
    hasReport &&
    isReportViewable &&
    referringPhysicianCanViewReport;
  const shouldShowForRadiologist = isRadiologist && shouldShowReportButton;
  const shouldShowForAdmin = isAdminOrSuperAdmin && !patientMode && hasReport && isReportViewable;
  const shouldShowForTechnician =
    isTechnician && !patientMode && hasReport && isReportViewable;
  const shouldShowForPatient = patientMode && hasReport && isReportViewable;

  // Don't render if conditions not met
  if (
    !shouldShowForRadiologist &&
    !shouldShowForReferringPhysician &&
    !shouldShowForAdmin &&
    !shouldShowForTechnician &&
    !shouldShowForPatient
  ) {
    return null;
  }

  const showViewButton =
    shouldShowForReferringPhysician ||
    shouldShowForAdmin ||
    shouldShowForTechnician ||
    shouldShowForPatient ||
    (shouldShowForRadiologist && hasReport);
  const showGenerateButton = shouldShowForRadiologist && !hasReport;

  const reportActionButtonClassName =
    'viewer-ris-header-action-btn viewer-ris-header-typography inline-flex min-h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-transparent bg-[#0891B2] px-3 py-1.5 text-white shadow-sm transition-colors hover:bg-[#0b7f9d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0891B2]/40';

  const viewLabel = isLoading ? t('Loading') : t('ViewReport');
  const generateLabel = isLoading ? t('Loading') : t('GenerateReport');

  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      {showViewButton ? (
        <button
          type="button"
          aria-label={viewLabel}
          title={viewLabel}
          className={`${reportActionButtonClassName} min-w-0 sm:min-w-[6rem]`}
          onClick={handleClick}
        >
          <Icons.FileText className="h-4 w-4 shrink-0 text-white" aria-hidden />
          <span className="viewer-ris-header-action-label">{viewLabel}</span>
        </button>
      ) : null}
      {showGenerateButton ? (
        <button
          type="button"
          aria-label={generateLabel}
          title={generateLabel}
          className={`${reportActionButtonClassName} min-w-0 sm:min-w-[8.875rem]`}
          onClick={handleClick}
        >
          <Icons.FileText className="h-4 w-4 shrink-0 text-white" aria-hidden />
          <span className="viewer-ris-header-action-label">{generateLabel}</span>
        </button>
      ) : null}
    </div>
  );
}

export default HeaderReportInfo;
